import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useLang } from "@/context/LangContext";
import { MAP_PARTY_COLORS, partyLabel, pick } from "@/lib/insights";
import { factHeadline as headline } from "@/components/FunFactsTicker";

function SeatsByPartyChart({ seats }) {
  const { t, lang } = useLang();
  const data = useMemo(() => {
    const c = {};
    for (const s of seats) {
      for (const [y, side] of [["y2021", s.y2021], ["y2026", s.y2026]]) {
        if (!side) continue;
        c[side.party_short] = c[side.party_short] || { party: side.party_short, y2021: 0, y2026: 0 };
        c[side.party_short][y] += 1;
      }
    }
    return Object.values(c).sort((a, b) => b.y2026 + b.y2021 - (a.y2026 + a.y2021));
  }, [seats]);

  return (
    <div className="bg-white rounded-[22px] p-5 sm:p-6" data-testid="seats-by-party-chart">
      <h3 className={`text-lg font-semibold text-[#1a1640] ${lang === "ta" ? "font-tamil" : "font-display"}`}>{t("seats_by_party_title")}</h3>
      <div className="mt-4 h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ left: -18, right: 6 }}>
            <CartesianGrid vertical={false} stroke="#eee" />
            <XAxis dataKey="party" tickFormatter={(p) => partyLabel(p, lang)} tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={(p) => partyLabel(p, lang)} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span style={{ color: "#1a1640" }}>{v}</span>} />
            <Bar dataKey="y2021" name="2021" fill="#b8b4d6" radius={[4, 4, 0, 0]} />
            <Bar dataKey="y2026" name="2026" fill="#1a1640" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className={`mt-2 text-[11.5px] text-[#8c89ad] ${lang === "ta" ? "font-tamil" : ""}`}>{t("seats_by_party_note")}</p>
    </div>
  );
}

function FactCard({ f }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  return (
    <article className="bg-white rounded-[22px] p-5 card-in flex flex-col" data-testid={`fact-card-${f.n}`}>
      <div className={`text-[11px] uppercase tracking-[0.18em] text-[#8c89ad] font-semibold ${ta ? "font-tamil" : ""}`}>{pick(f, "category", lang)}</div>
      <div className="mt-2 font-display text-3xl font-semibold text-[#1a1640] tabular-nums break-words" data-testid={`fact-headline-${f.n}`}>{headline(f, lang, t)}</div>
      <h4 className={`mt-2 text-[15px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{pick(f, "fact", lang)}</h4>
      <p className={`mt-1.5 text-[13.5px] leading-relaxed text-[#4b4870] ${ta ? "font-tamil" : ""}`}>{pick(f, "detail", lang)}</p>
    </article>
  );
}

export default function FactsTab({ data, seats }) {
  const { t, lang } = useLang();
  const ta = lang === "ta";
  const [cat, setCat] = useState("all");
  const cats = [...new Set(data.facts.map((f) => f.category))];
  const shown = cat === "all" ? data.facts : data.facts.filter((f) => f.category === cat);
  const catLabel = (c) => pick(data.facts.find((f) => f.category === c), "category", lang);
  const chip = (k, label) => (
    <button
      key={k}
      onClick={() => setCat(k)}
      className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold transition-colors ${cat === k ? "bg-[#1a1640] text-white" : "bg-white text-[#1a1640] hover:bg-[#f3eedc]"} ${ta ? "font-tamil" : ""}`}
      data-testid={`fact-cat-${k.replace(/\W+/g, "-").toLowerCase()}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {seats.length > 0 && <SeatsByPartyChart seats={seats} />}
      <div className="flex flex-wrap gap-2" data-testid="fact-categories">
        {chip("all", t("all_label"))}
        {cats.map((c) => chip(c, catLabel(c)))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fact-grid">
        {shown.map((f) => <FactCard key={f.n} f={f} />)}
      </div>
      <div className="bg-white/70 rounded-[22px] p-5" data-testid="facts-method">
        <h4 className={`text-[14px] font-bold text-[#1a1640] ${ta ? "font-tamil" : ""}`}>{t("method_title")}</h4>
        <ul className={`mt-2 space-y-1.5 text-[12.5px] text-[#4b4870] list-disc pl-5 ${ta ? "font-tamil" : ""}`}>
          {data.method.map((m, i) => <li key={i}>{pick(m, "text", lang)}</li>)}
        </ul>
      </div>
    </div>
  );
}
